import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, take, switchMap, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectPassportMenu } from '../../store/settings/settings.selectors';
import { UrlService } from '../services/url.service';

@Injectable({
  providedIn: 'root',
})
export class MenuAccessGuard implements CanActivate {
  // Mapping des paths vers les titres des menus dans les settings
  private readonly pathToMenuTitle: Record<string, string> = {
    appointments: 'APPOINTMENTS',
    'patient-record': 'PATIENT_RECORD',
    insurances: 'INSURANCES',
    authorizations: 'AUTHORIZATIONS',
    'health-tracking': 'APPOINTMENTS', // health-tracking correspond à APPOINTMENTS selon la correction
    payments: 'PAYMENTS',
  };

  constructor(
    private store: Store,
    private router: Router,
    private urlService: UrlService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const path = route.routeConfig?.path || '';
    
    // Extraire le path de base (sans paramètres) pour les routes comme "insurances/:contractId"
    const basePath = path.split('/')[0];
    const menuTitle = this.pathToMenuTitle[basePath] || this.pathToMenuTitle[path];

    // Si le path n'est pas dans le mapping, autoriser l'accès (pas de restriction)
    if (!menuTitle) {
      return of(true);
    }

    // Vérifier si le menu est activé dans les settings
    return this.store.select(selectPassportMenu).pipe(
      take(1),
      switchMap((passportMenu) => {
        // Si pas encore chargé, attendre le chargement
        if (!passportMenu || passportMenu.length === 0) {
          return this.store.select(selectPassportMenu).pipe(
            filter((menu): menu is Array<{
              title: string;
              isVisible: boolean;
              order: number;
              _id: string;
            }> => !!menu && menu.length > 0),
            take(1),
            map((menu) => this.checkMenuAccess(menu, menuTitle))
          );
        }
        return of(this.checkMenuAccess(passportMenu, menuTitle));
      })
    );
  }

  private checkMenuAccess(
    passportMenu: Array<{
      title: string;
      isVisible: boolean;
      order: number;
      _id: string;
    }>,
    menuTitle: string
  ): boolean {
    const menu = passportMenu.find((m) => m.title === menuTitle);

    // Si le menu n'existe pas dans les settings, autoriser l'accès par défaut
    if (!menu) {
      return true;
    }

    // Si le menu est visible, autoriser l'accès
    if (menu.isVisible) {
      return true;
    }

    // Si le menu est désactivé, rediriger vers la page 403
    this.router.navigate(this.urlService.buildRouteSegments('403'));
    return false;
  }
}
